import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserRole, User } from '@/types/auth'
import { ROLE_PERMISSIONS, hasPermission, canAccessAdminPanel } from '@/types/auth'

export const useUserStore = defineStore('user', () => {
  const token = ref<string | null>(localStorage.getItem('token'))
  const userInfo = ref<User | null>(null)

  // Load user info from localStorage on initialization
  const loadFromStorage = () => {
    const storedInfo = localStorage.getItem('userInfo')
    if (storedInfo) {
      try {
        userInfo.value = JSON.parse(storedInfo)
      } catch (error) {
        console.error('Failed to parse userInfo from storage:', error)
        userInfo.value = null
      }
    }
  }

  function setToken(newToken: string) {
    token.value = newToken
    localStorage.setItem('token', newToken)
  }

  function setUserInfo(info: User) {
    userInfo.value = info
    localStorage.setItem('userInfo', JSON.stringify(info))
  }

  function logout() {
    token.value = null
    userInfo.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('rememberedUsername')
  }

  // Permission checkers
  const currentRole = computed(() => userInfo.value?.role || 'user')
  const hasRolePermission = computed(() => (permission: string) => {
    return hasPermission(currentRole.value, permission)
  })
  const canAccessAdmin = computed(() => {
    return canAccessAdminPanel(currentRole.value)
  })
  const isAdmin = computed(() => {
    return currentRole.value === 'super_admin' || currentRole.value === 'admin'
  })
  const isSuperAdmin = computed(() => {
    return currentRole.value === 'super_admin'
  })

  const isAuthenticated = computed(() => !!token.value)

  return {
    token,
    userInfo,
    currentRole,
    hasRolePermission,
    canAccessAdmin,
    isAdmin,
    isSuperAdmin,
    isAuthenticated,
    setToken,
    setUserInfo,
    loadFromStorage,
    logout,
  }
})
